import { z } from 'zod';

export const TOOL_STATUS_THRESHOLDS = {
  CHANGE: 10,
  ORDER:  30,
  WATCH:  60,
} as const;

export const TOOL_CHANGE_METHODS = [
  'tlms_reset',
  'wear_offset_reset',
  'operator_registered',
  'program_comment',
  'inferred',
] as const;

export const TOOL_CONFIDENCE_LEVELS = [
  'high',
  'medium',
  'low',
] as const;

export const TOOL_TYPES = [
  'endmill',
  'drill',
  'boring_bar',
  'insert',
  'tap',
  'reamer',
  'face_mill',
  'other',
] as const;

export const TOOL_MATERIALS = [
  'carbide',
  'hss',
  'ceramic',
  'cbn',
  'pcd',
  'unknown',
] as const;

export type ToolChangeMethod    = (typeof TOOL_CHANGE_METHODS)[number];
export type ToolConfidenceLevel = (typeof TOOL_CONFIDENCE_LEVELS)[number];
export type ToolType            = (typeof TOOL_TYPES)[number];
export type ToolMaterial        = (typeof TOOL_MATERIALS)[number];
export type ToolStatus          = 'change' | 'order' | 'watch' | 'good';

// ── MQTT payload (published by dongle/hub) ────────────────────────────────────

const toolEntrySchema = z.object({
  tool_number:            z.number().int().min(1).max(99),
  life_percent:           z.number().finite().min(0).max(100),
  remaining_cuts:         z.number().int().min(0).nullable(),
  total_cuts:             z.number().int().min(0).nullable(),
  wear_offset_mm:         z.number().finite().nullable(),
  previous_wear_offset:   z.number().finite().nullable(),
  tlms_available:         z.boolean(),
  last_changed_at:        z.string().datetime({ offset: true }).nullable(),
  change_detected_method: z.enum(TOOL_CHANGE_METHODS).nullable(),
});

export const toolLifeMqttPayloadSchema = z.object({
  device_id:       z.string().min(1).max(64),
  machine_id:      z.string().min(1).max(64),
  shop_id:         z.string().min(1).max(64),
  controller_type: z.string().min(1),
  timestamp:       z.string().datetime({ offset: true }),
  fw_version:      z.string().max(32),
  tools:           z.array(toolEntrySchema).min(1).max(99),
});

export type ToolLifeMqttPayload = z.infer<typeof toolLifeMqttPayloadSchema>;
export type ToolEntry           = z.infer<typeof toolEntrySchema>;

// ── DynamoDB record: tool slot state ─────────────────────────────────────────

export const toolSlotRecordSchema = z.object({
  PK:                     z.string(),
  SK:                     z.string(),
  GSI1PK:                 z.string(),
  GSI1SK:                 z.string(),
  entity_type:            z.literal('TOOL_SLOT'),
  shop_id:                z.string(),
  machine_id:             z.string(),
  tool_number:            z.number().int(),
  life_percent:           z.number(),
  remaining_cuts:         z.number().int().nullable(),
  total_cuts:             z.number().int().nullable(),
  wear_offset_mm:         z.number().nullable(),
  status:                 z.enum(['change', 'order', 'watch', 'good']),
  confidence:             z.enum(TOOL_CONFIDENCE_LEVELS),
  change_detected_method: z.enum(TOOL_CHANGE_METHODS).nullable(),
  tool_library_id:        z.string().nullable(),
  assigned_at:            z.string().datetime({ offset: true }).nullable(),
  last_changed_at:        z.string().datetime({ offset: true }).nullable(),
  updated_at:             z.string().datetime({ offset: true }),
  created_at:             z.string().datetime({ offset: true }),
});

export type ToolSlotRecord = z.infer<typeof toolSlotRecordSchema>;

// ── DynamoDB record: tool library (shop catalog) ──────────────────────────────

export const toolLibraryRecordSchema = z.object({
  PK:                    z.string(),
  SK:                    z.string(),
  GSI1PK:                z.string(),
  GSI1SK:                z.string(),
  entity_type:           z.literal('TOOL_LIBRARY'),
  tool_id:               z.string().uuid(),
  shop_id:               z.string(),
  description:           z.string(),
  manufacturer:          z.string().nullable(),
  part_number:           z.string().nullable(),
  type:                  z.enum(TOOL_TYPES),
  material:              z.enum(TOOL_MATERIALS),
  diameter_mm:           z.number().positive().nullable(),
  expected_life_cuts:    z.number().int().positive().nullable(),
  expected_life_minutes: z.number().positive().nullable(),
  notes:                 z.string().nullable(),
  created_at:            z.string().datetime({ offset: true }),
  updated_at:            z.string().datetime({ offset: true }),
});

export type ToolLibraryRecord = z.infer<typeof toolLibraryRecordSchema>;

// ── API response shapes ───────────────────────────────────────────────────────

export const toolSlotResponseSchema = z.object({
  tool_number:            z.number().int(),
  life_percent:           z.number(),
  remaining_cuts:         z.number().int().nullable(),
  total_cuts:             z.number().int().nullable(),
  wear_offset_mm:         z.number().nullable(),
  status:                 z.enum(['change', 'order', 'watch', 'good']),
  confidence:             z.enum(TOOL_CONFIDENCE_LEVELS),
  change_detected_method: z.enum(TOOL_CHANGE_METHODS).nullable(),
  last_changed_at:        z.string().nullable(),
  updated_at:             z.string(),
  tool_definition:        toolLibraryRecordSchema.nullable(),
});

export type ToolSlotResponse = z.infer<typeof toolSlotResponseSchema>;

export const toolListResponseSchema = z.object({
  machine_id:   z.string(),
  tools:        z.array(toolSlotResponseSchema),
  has_identity: z.boolean(),
  retrieved_at: z.string(),
  message:      z.string().nullable(),
});

export type ToolListResponse = z.infer<typeof toolListResponseSchema>;

// ── DynamoDB record: graveyard (retired slot assignments) ─────────────────────

export const toolGraveyardRecordSchema = z.object({
  PK:                          z.string(),
  SK:                          z.string(),
  entity_type:                 z.literal('TOOL_GRAVEYARD'),
  graveyard_id:                z.string().uuid(),
  shop_id:                     z.string(),
  tool_library_id:             z.string(),
  machine_id:                  z.string(),
  tool_number:                 z.number().int(),
  life_percent_at_removal:     z.number(),
  assigned_at:                 z.string().datetime({ offset: true }).nullable(),
  removed_at:                  z.string().datetime({ offset: true }),
  replaced_by_tool_library_id: z.string().nullable(),
});

export type ToolGraveyardRecord = z.infer<typeof toolGraveyardRecordSchema>;

// ── DynamoDB record: tool holder (physical tool instance) ─────────────────────

export const toolHolderRecordSchema = z.object({
  PK:                 z.string(),
  SK:                 z.string(),
  entity_type:        z.literal('TOOL_HOLDER'),
  holder_id:          z.string().uuid(),
  shop_id:            z.string(),
  description:        z.string(),
  manufacturer:       z.string().nullable(),
  part_number:        z.string().nullable(),
  type:               z.enum(TOOL_TYPES),
  material:           z.enum(TOOL_MATERIALS),
  diameter_mm:        z.number().positive().nullable(),
  length_mm:          z.number().positive().nullable(),
  expected_life_cuts: z.number().int().positive().nullable(),
  created_at:         z.string().datetime({ offset: true }),
  updated_at:         z.string().datetime({ offset: true }),
});

export type ToolHolderRecord = z.infer<typeof toolHolderRecordSchema>;

// ── DynamoDB record: tool checkout (borrow event) ─────────────────────────────

export const toolCheckoutRecordSchema = z.object({
  PK:               z.string(),
  SK:               z.string(),
  entity_type:      z.literal('TOOL_CHECKOUT'),
  checkout_id:      z.string().uuid(),
  shop_id:          z.string(),
  holder_id:        z.string(),
  checked_out_by:   z.string(),
  checked_out_at:   z.string().datetime({ offset: true }),
  from_machine_id:  z.string(),
  from_slot:        z.number().int(),
  status:           z.enum(['open', 'closed']),
  checked_in_at:    z.string().datetime({ offset: true }).nullable(),
  geometry_changed: z.boolean().nullable(),
  new_length_mm:    z.number().positive().nullable(),
  notes:            z.string().nullable(),
});

export type ToolCheckoutRecord = z.infer<typeof toolCheckoutRecordSchema>;

// ── DynamoDB record: pocket rule (slot constraints) ───────────────────────────

export const toolPocketRuleRecordSchema = z.object({
  PK:                  z.string(),
  SK:                  z.string(),
  entity_type:         z.literal('POCKET_RULE'),
  shop_id:             z.string(),
  machine_id:          z.string(),
  slot_number:         z.number().int(),
  allowed_types:       z.array(z.enum(TOOL_TYPES)),
  max_diameter_mm:     z.number().positive().nullable(),
  designation:         z.enum(['dedicated', 'flexible']),
  dedicated_holder_id: z.string().nullable(),
  updated_at:          z.string().datetime({ offset: true }),
});

export type ToolPocketRuleRecord = z.infer<typeof toolPocketRuleRecordSchema>;

// ── Validate-program response ─────────────────────────────────────────────────

export const programWarningSchema = z.object({
  type:        z.enum(['wrong_tool_in_slot', 'missing_prerequisite', 'tool_checked_out', 'geometry_mismatch']),
  tool_number: z.number().int().optional(),
  message:     z.string(),
});

export type ProgramWarning = z.infer<typeof programWarningSchema>;

export const validateProgramResponseSchema = z.object({
  valid:    z.boolean(),
  warnings: z.array(programWarningSchema),
});

export type ValidateProgramResponse = z.infer<typeof validateProgramResponseSchema>;
